/**
 * After a customer accepts a proposal (and pays the deposit when one is
 * due), send them to the order tracker scheduling step — not back to the
 * letter they already signed.
 */

import type { CustomerOrderPrimaryAction } from "@/lib/virtualspace/order-customer-status";

export const ORDER_TRACKER_SCHEDULING_ANCHOR = "scheduling";

export function orderTrackerSharePath(orderToken: string): string {
  return `/share/order/${encodeURIComponent(orderToken)}`;
}

export function orderTrackerSharePasswordPath(orderToken: string): string {
  return `${orderTrackerSharePath(orderToken)}/password`;
}

/** Pay now on the public tracker — retrieves a live Stripe hosted URL. */
export function orderTrackerInvoicePayPath(orderToken: string): string {
  return `/api/share/order/${encodeURIComponent(orderToken)}/pay`;
}

export function orderTrackerInvoicePayFallbackPath(orderToken: string): string {
  return `${orderTrackerSharePath(orderToken)}#billing`;
}

/**
 * Why Pay now sent the customer back to the tracker instead of to a payment
 * page. Codes only: the tracker owns the customer copy, so no internal or
 * provider error text ever reaches a public token holder.
 */
export type OrderTrackerPayNotice =
  | "paused"
  | "settling"
  | "clearing"
  | "use_card"
  | "unavailable";

export const ORDER_TRACKER_PAY_NOTICE_PARAM = "payNotice";

const PAY_NOTICE_COPY: Record<OrderTrackerPayNotice, string> = {
  paused:
    "Online payment is paused for a moment. Try again later, or reply to your invoice email for help.",
  settling:
    "Your earlier card payment is still being confirmed. No second payment is needed; this page updates once it settles.",
  clearing:
    "A payment for this invoice is already clearing, which takes about four business days. Wait for it to clear before paying again; this page updates once it does.",
  use_card: "This invoice is paid by card. Use Pay by card below.",
  unavailable:
    "We couldn't open the payment page just now. Try again in a minute, or reply to your invoice email for help.",
};

/** Back to the tracker's billing section with a customer-safe notice. */
export function orderTrackerPayNoticePath(
  orderToken: string,
  notice: OrderTrackerPayNotice,
): string {
  return `${orderTrackerSharePath(orderToken)}?${ORDER_TRACKER_PAY_NOTICE_PARAM}=${notice}#billing`;
}

/**
 * Card checkout refusals the customer can act on. Anything else becomes
 * "try again": raw provider or database text never reaches a public token
 * holder.
 */
export function orderTrackerPayErrorNotice(
  error: unknown,
): OrderTrackerPayNotice | "no_balance" {
  const message = error instanceof Error ? error.message : "";
  // A bank payment clearing on Stripe (card checkout refuses meanwhile).
  if (/already clearing/i.test(message)) return "clearing";
  if (/no second payment|previous card payment is settling/i.test(message))
    return "settling";
  if (/no balance to pay/i.test(message)) return "no_balance";
  return "unavailable";
}

export type OrderTrackerPaymentNotice = {
  tone: "success" | "info";
  text: string;
};

/**
 * The payment banner for the tracker from its query: the return from a card
 * Checkout (`?cardPayment=`) or a Pay now bounce (`?payNotice=`). Unknown
 * values show nothing. A `success` return says "processing", never "paid":
 * settlement is webhook-only and the param is caller-controlled.
 */
export function orderTrackerPaymentNotice(search: {
  cardPayment?: string | string[];
  payNotice?: string | string[];
}): OrderTrackerPaymentNotice | null {
  const card = firstSearchValue(search.cardPayment);
  if (card === "success")
    return {
      tone: "success",
      text: "Thank you. Your card payment is processing. No second payment is needed; this page updates once it settles.",
    };
  if (card === "cancelled")
    return {
      tone: "info",
      text: "Card payment cancelled. You have not been charged.",
    };
  const notice = firstSearchValue(search.payNotice);
  if (notice && Object.hasOwn(PAY_NOTICE_COPY, notice))
    return {
      tone: "info",
      text: PAY_NOTICE_COPY[notice as OrderTrackerPayNotice],
    };
  return null;
}

export function customerOrderPrimaryActionHref(
  action: NonNullable<CustomerOrderPrimaryAction>,
  orderToken: string,
): string {
  if (action.kind === "pay") return orderTrackerInvoicePayPath(orderToken);
  return `#${action.anchor}`;
}

export function orderTrackerAfterAcceptPath(orderToken: string): string {
  return `${orderTrackerSharePath(orderToken)}?proposal_accepted=1`;
}

export function orderTrackerAfterDepositSuccessPath(orderToken: string): string {
  return `${orderTrackerSharePath(orderToken)}?proposal_deposit=success`;
}

export function orderTrackerAfterDepositCancelPath(orderToken: string): string {
  return `${orderTrackerSharePath(orderToken)}?proposal_deposit=cancelled`;
}

/** SpaceManager order page after a signed-in accept. */
export function portalOrderAfterAcceptPath(portalOrderPath: string): string {
  const joiner = portalOrderPath.includes("?") ? "&" : "?";
  return `${portalOrderPath}${joiner}proposal_accepted=1`;
}

/**
 * Where the customer lands after accepting.
 *
 *   1. A signed-in SpaceManager customer with access to the order — or a
 *      visitor who arrived from the portal (`?from=vs`) — goes back to
 *      the SpaceManager order page, not the public tracker. Sending a
 *      logged-in customer to the public letter and then to the public
 *      tracker was the "in circles" loop (2026-09-03).
 *   2. Otherwise the public order tracker's scheduling step, when the
 *      order has a share token.
 *   3. Otherwise stay on the letter (undefined) and show the accepted
 *      state — never a `notFound()` tracker.
 */
export function resolveAfterAcceptHref(args: {
  portalOrderPath: string | null;
  preferPortal: boolean;
  trackerToken: string | null;
}): string | undefined {
  if (args.preferPortal && args.portalOrderPath) {
    return portalOrderAfterAcceptPath(args.portalOrderPath);
  }
  if (args.trackerToken) return orderTrackerAfterAcceptPath(args.trackerToken);
  return undefined;
}

function firstSearchValue(
  value: string | string[] | undefined,
): string | undefined {
  if (Array.isArray(value)) return value[0];
  return value;
}

/** Stripe success and no-deposit accept both land here. */
export function searchRequestsSchedulingFocus(search: {
  proposal_accepted?: string | string[];
  proposal_deposit?: string | string[];
}): boolean {
  const accepted = firstSearchValue(search.proposal_accepted);
  const deposit = firstSearchValue(search.proposal_deposit);
  return accepted === "1" || deposit === "success";
}

export function trackerSchedulingFocusAnchor(
  primaryAction: CustomerOrderPrimaryAction,
): string {
  if (!primaryAction) return ORDER_TRACKER_SCHEDULING_ANCHOR;
  switch (primaryAction.kind) {
    case "schedule_help":
    case "confirm_time":
    case "review_times":
    case "add_contact":
      return primaryAction.anchor;
    case "pay":
    case "review_proposal":
      return ORDER_TRACKER_SCHEDULING_ANCHOR;
    default: {
      const _exhaustive: never = primaryAction.kind;
      return _exhaustive;
    }
  }
}

/** Prefer the live CTA, then the visit/sites wrappers. */
export function orderTrackerSchedulingFocusCandidates(
  preferredAnchor: string,
): string[] {
  const extras = [
    ORDER_TRACKER_SCHEDULING_ANCHOR,
    "schedule-help",
    "sites-needs-attention",
    "confirm-time",
  ];
  return [
    preferredAnchor,
    ...extras.filter((id) => id !== preferredAnchor),
  ];
}

export function orderTrackerAfterAcceptNotice(args: {
  proposalAccepted: boolean;
  depositSuccess: boolean;
  depositPaid: boolean;
}): string | null {
  if (args.depositSuccess) {
    return args.depositPaid
      ? "Deposit received. Next, pick a visit time."
      : "Payment received — your deposit is being confirmed. Next, pick a visit time.";
  }
  if (args.proposalAccepted) {
    return "Proposal accepted. Next, pick a visit time.";
  }
  return null;
}
